import Decimal from 'decimal.js';
import { useMemo } from 'react';

import { TileNode } from '@/components/diagrams/FlowChart';
import { useNode } from '@/components/diagrams/FlowChart/hooks/useNode';
import {
  buildStateTaxLines,
  useResidentialStateForDeath,
} from '@/modules/taxes/nonResidentialState.utils';
import { AfterDeath, StateCode } from '@/types/schema';

import { getTaxStateAndFederalAndGst } from '../EstateWaterfall.utils';
import { EstateWaterfall_TaxSummaryV2Fragment } from '../graphql/EstateWaterfall.generated';

interface UseTaxBreakoutOptions {
  afterDeath?: AfterDeath;
  firstGrantorDeathId?: string | null;
}

export function useTaxBreakout(
  summary: EstateWaterfall_TaxSummaryV2Fragment,
  options?: UseTaxBreakoutOptions
) {
  const node = useNode<TileNode>();

  // Returns undefined when no household is in context (compare modal,
  // presentation rendering) or when AfterDeath is not provided.
  const derivedResidentialStateCode = useResidentialStateForDeath({
    afterDeath: options?.afterDeath ?? AfterDeath.None,
    firstGrantorDeathId: options?.firstGrantorDeathId,
  });
  const residentialStateCode = options?.afterDeath
    ? derivedResidentialStateCode
    : undefined;

  const {
    federal,
    state,
    gst: gstTax,
    inheritanceTax,
  } = getTaxStateAndFederalAndGst(summary);

  const states = summary?.stateTax?.flatMap((s) => s ?? []);
  const inheritanceTaxEntries =
    summary?.stateInheritanceTax?.flatMap((s) => s ?? []) ?? [];

  const showTaxBreakout =
    node.data.estateWaterfallConfig?.showTaxBreakout &&
    (state.greaterThan(0) ||
      federal.greaterThan(0) ||
      gstTax.greaterThan(0) ||
      inheritanceTax.greaterThan(0));

  const stateTaxesByCode = (states || []).reduce(
    (acc, state) => {
      if (state?.stateCode) {
        acc[state.stateCode] = acc[state.stateCode] ?? new Decimal(0);
        acc[state.stateCode] = acc[state.stateCode].plus(state.tax);
      }
      return acc;
    },
    {} as Record<StateCode, Decimal>
  );

  const inheritanceTaxesByCode = inheritanceTaxEntries.reduce(
    (acc, entry) => {
      if (entry?.stateCode) {
        acc[entry.stateCode] = acc[entry.stateCode] ?? new Decimal(0);
        acc[entry.stateCode] = acc[entry.stateCode].plus(entry.tax);
      }
      return acc;
    },
    {} as Record<StateCode, Decimal>
  );

  const residentialStateCodes = useMemo(
    () =>
      residentialStateCode
        ? new Set<StateCode>([residentialStateCode])
        : new Set<StateCode>(),
    [residentialStateCode]
  );

  const otherTaxLineCount =
    (gstTax.greaterThan(0) ? 1 : 0) +
    (federal.greaterThan(0) ? 1 : 0) +
    Object.values(inheritanceTaxesByCode).filter((t) => t.greaterThan(0))
      .length;

  const displayStateTaxLines = buildStateTaxLines({
    stateTaxByCode: stateTaxesByCode,
    residentialStateCodes,
    otherTaxLineCount,
  });

  return {
    stateTaxesByCode,
    inheritanceTaxesByCode,
    showTaxBreakout,
    federal,
    state,
    inheritanceTax,
    node,
    gstTax,
    /**
     * State estate-tax lines in display order (residential first, then
     * non-residential alphabetical), with the per-tile budget consolidation
     * applied.
     */
    displayStateTaxLines,
  };
}
